<template>
  <sidebar
    :addTitle="$t('MRMR.Entry.AddNew')"
    :editTitle="$t('MRMR.Entry.Edit')"
    :isSidebarActive="isSidebarActive"
    :data="data"
      @onNewData="newData"
      @onEditData="editData"
      @closeSidebar="closeSidebar">

    <ValidationObserver v-slot="{ handleSubmit }">
      <form-wizard
          ref="wizard"
          color="rgba(var(--vs-primary), 1)"
          errorColor="rgba(var(--vs-danger), 1)"
          :title="null"
          :subtitle="null"
          :nextButtonText="$t('Next')"
          :backButtonText="$t('Back')"
          :finishButtonText="$t('Submit')"
          @on-complete="handleSubmit(submitData)"
        >
        <tab-content :title="$t('MRMR.Entry.Step1')" class="mb-2" :before-change="validateStep1">
          <ValidationObserver ref="step1Observer" >
            <!-- Base scenario content -->
            <form data-vv-scope="step-1">
              <vs-row class="mb-3">
                <vs-col vs-w="12">
                  <form-label>{{$t('MRMR.Entry.Area')}}</form-label>
                  <ValidationProvider :name="$t('MRMR.Entry.Area')" rules="required" v-slot="{ errors }">
                     <v-select
                      taggable
                      :options="suggestedAreas"
                      v-model="model.area"
                      class="w-full"
                    />
                    <validation-message
                      >{{ errors[0] }}</validation-message>
                  </ValidationProvider>
                </vs-col>
              </vs-row>
              <vs-row class="mb-3">
                <vs-col vs-w="12">
                  <form-label>{{$t('MRMR.Entry.ResourceType')}}</form-label>
                  <ValidationProvider :name="$t('MRMR.Entry.ResourceType')" rules="required" v-slot="{ errors }">
                     <v-select
                        label="name"
                        :options="resourceTypes"
                        v-model="model.resourceType"
                        :reduce="r => r.id"
                        class="w-full"
                      />
                    <validation-message
                        >{{ errors[0] }}</validation-message>
                  </ValidationProvider>
                </vs-col>
              </vs-row>
              <vs-row class="mb-3">
                <vs-col vs-w="12">
                  <form-label>{{$t('MRMR.Entry.MiningType')}}</form-label>
                  <ValidationProvider :name="$t('MRMR.Entry.MiningType')" rules="required" v-slot="{ errors }">
                     <v-select
                        label="name"
                        :options="miningTypes"
                        v-model="model.miningType"
                        :reduce="r => r.id"
                        class="w-full"
                      />
                    <validation-message
                        >{{ errors[0] }}</validation-message>
                  </ValidationProvider>
                </vs-col>
              </vs-row>
              <vs-row class="mb-3">
                <vs-col vs-w="12">
                  <form-label>{{$t('MRMR.Entry.Elements')}}</form-label>
                  <ValidationProvider :name="$t('MRMR.Entry.Elements')" v-slot="{ errors }">
                     <v-select
                        label="name"
                        :options="elements"
                        v-model="model.elements"
                        :multiple="true"
                        :clearable="true"
                        :reduce="r => r.id"
                        :placeholder="$t('MRMR.Entry.AllElements')"
                        class="w-full"
                      />
                    <validation-message
                        >{{ errors[0] }}</validation-message>
                  </ValidationProvider>
                </vs-col>
              </vs-row>
              <vs-row class="mb-3">
                <vs-col vs-w="12">
                  <vs-switch class="mt-1 switch_tonnage" color="success" v-model="tonnageMode">
                    <span slot="on">{{ $t('MRMR.Entry.Tonnage') }}</span>
                    <span slot="off">{{ $t('MRMR.Entry.Volume') + "-" + $t('MRMR.Entry.Density') }}</span>
                  </vs-switch>
                </vs-col>
              </vs-row>
              <vs-row v-if="!tonnageMode" class="mb-3">
                <vs-col vs-w="5" class="mr-5">
                  <form-label>{{$t('MRMR.Entry.Volume')}}</form-label>
                  <ValidationProvider :name="$t('MRMR.Entry.Volume')" rules="required|min_value:0" v-slot="{ errors }">
                    <vue-numeric class="vs-inputx vs-input--input normal hasValue" 
                      separator="," 
                      v-model="model.volume"></vue-numeric>
                    <validation-message
                      >{{ errors[0] }}</validation-message>
                  </ValidationProvider>
                </vs-col>
                <vs-col vs-w="6" >
                  <ValidationProvider :name="$t('MRMR.Entry.Density')" rules="required|min_value:0" v-slot="{ errors }">
                    <validated-input
                      :label="$t('MRMR.Entry.Density')"
                      v-model="model.density"
                    ></validated-input>
                    <validation-message
                      >{{ errors[0] }}</validation-message>
                  </ValidationProvider>
                </vs-col>
              </vs-row>
              <vs-row  class="mb-3">
                <vs-col v-if="!tonnageMode"  vs-w="5" class="mr-5">
                  <form-label>{{$t('MRMR.Entry.Tonnage')}}</form-label>
                  <span class="previousGrade">{{ model.tonnage  | toMRMRTonnage }}</span>
                </vs-col>
                 <vs-col v-else  vs-w="5" class="mr-5">
                  <form-label>{{$t('MRMR.Entry.Tonnage')}}</form-label>
                  <ValidationProvider :name="$t('MRMR.Entry.Tonnage')" rules="required|min_value:0" v-slot="{ errors }">
                    <vue-numeric class="vs-inputx vs-input--input normal hasValue" 
                      separator="," 
                      v-model="model.tonnage"></vue-numeric>
                    <validation-message
                      >{{ errors[0] }}</validation-message>
                  </ValidationProvider>
                </vs-col>
                 <vs-col vs-w="6" v-if="previousTonnageStats" >
                   <form-label>{{$t('MRMR.Entry.PreviousTonnage')}}</form-label>
                   <span class="previousGrade">{{ previousTonnageStats  | toMRMRTonnage }}</span>
                </vs-col>
              </vs-row>
              <validation-message>{{ step1Error }}</validation-message>
            </form>
          </ValidationObserver>
        </tab-content>
        <tab-content :title="$t('MRMR.Entry.Step2')" class="mb-2" :before-change="validateStep2">
          <ValidationObserver ref="step2Observer" >
            <form data-vv-scope="step-2">
              <vs-row vs-type="flex" vs-justify="space-between">
                <vs-col vs-w="6" >
                  <form-label>{{$t('MRMR.Entry.EntryMode')}}</form-label>
                  <vs-switch class="mt-2 switch_grade" color="primary" icon-pack="feather" v-model="useGradeEntry">
                    <template #on>
                      {{$t('MRMR.Entry.EntryModeGrade')}}
                    </template>
                    <template #off>
                      {{$t('MRMR.Entry.EntryModeQuantity')}}
                    </template>
                  </vs-switch>
                </vs-col>
              </vs-row>
              <vs-row class="mb-1" vs-type="flex" vs-justify="space-between">
                <vs-divider position="left-center" class="mb-2">{{$t('MRMR.Entry.Gold')}}</vs-divider>
                <vs-col vs-w="5" class="mr-5">
                  <ValidationProvider :name="$t('MRMR.Entry.Grade')" rules="required|decimal:9|min_value:0" v-slot="{ errors }">
                    <validated-input
                      :label="$t('MRMR.Entry.Grade')"
                      v-model="entryGoldGrade"
                      :disabled="!useGradeEntry"
                    ></validated-input>
                    <validation-message
                      >{{ errors[0] }}</validation-message>
                  </ValidationProvider>
                  <ValidationProvider :name="$t('MRMR.Entry.Grams')" rules="required|decimal:9|min_value:0" v-slot="{ errors }">
                    <validated-input
                      :label="$t('MRMR.Entry.Grams')"
                      v-model="entryGoldGrams"
                      :disabled="useGradeEntry"
                    ></validated-input>
                    <validation-message
                      >{{ errors[0] }}</validation-message>
                  </ValidationProvider>
                </vs-col>
                <vs-col vs-w="6" v-if="previousGoldGradeStats" >
                   <form-label>{{$t('MRMR.Entry.PreviousGrade')}}</form-label>
                   <span class="previousGrade">{{ previousGoldGradeStats | toFriendlyGrade }}</span>
                </vs-col>
              </vs-row>
              <vs-row class="mb-1" vs-type="flex" vs-justify="space-between">
                <vs-divider position="left-center" class="mb-2">{{$t('MRMR.Entry.Silver')}}</vs-divider>
                  <vs-col vs-w="5" class="mr-5">
                  <ValidationProvider :name="$t('MRMR.Entry.Grade')" rules="decimal:9|min_value:0" v-slot="{ errors }">
                    <validated-input
                        :label="$t('MRMR.Entry.Grade')"
                      v-model="entrySilverGrade"
                      :disabled="!useGradeEntry"
                    ></validated-input>
                    <validation-message
                      >{{ errors[0] }}</validation-message>
                  </ValidationProvider>
                  <ValidationProvider :name="$t('MRMR.Entry.Grams')" rules="decimal:9|min_value:0" v-slot="{ errors }">
                    <validated-input
                      :label="$t('MRMR.Entry.Grams')"
                      v-model="entrySilverGrams"
                      :disabled="useGradeEntry"
                    ></validated-input>
                    <validation-message
                      >{{ errors[0] }}</validation-message>
                  </ValidationProvider>
                </vs-col>
                 <vs-col vs-w="6" v-if="previousSilverGradeStats !== null" >
                   <form-label>{{$t('MRMR.Entry.PreviousGrade')}}</form-label>
                   <span class="previousGrade">{{ previousSilverGradeStats | toFriendlyGrade }}</span>
                </vs-col>
              </vs-row>
              <vs-row class="mb-1" vs-type="flex" vs-justify="space-between">
                <vs-divider position="left-center" class="mb-2">{{$t('MRMR.Entry.Copper')}}</vs-divider>
                  <vs-col vs-w="5" class="mr-5">
                  <ValidationProvider :name="$t('MRMR.Entry.Grade')" rules="decimal:9|min_value:0" v-slot="{ errors }">
                    <validated-input
                        :label="$t('MRMR.Entry.Grade')"
                      v-model="entryCopperGrade"
                      :disabled="!useGradeEntry"
                    ></validated-input>
                    <validation-message
                      >{{ errors[0] }}</validation-message>
                  </ValidationProvider>
                  
                  <ValidationProvider :name="$t('MRMR.Entry.Kilograms')" rules="decimal:9|min_value:0" v-slot="{ errors }">
                    <validated-input
                      :label="$t('MRMR.Entry.Kilograms')"
                      v-model="entryCopperKg"
                      :disabled="useGradeEntry"
                    ></validated-input>
                    <validation-message
                      >{{ errors[0] }}</validation-message>
                  </ValidationProvider>
                </vs-col>
                <vs-col vs-w="6" v-if="previousCopperGrade" >
                   <form-label>{{$t('MRMR.Entry.PreviousGrade')}}</form-label>
                   <span class="previousGrade">{{ previousCopperGrade | toFriendlyGrade }}</span>
                </vs-col>
              </vs-row>
              <vs-row class="mb-1" vs-type="flex" vs-justify="space-between">
                <vs-divider position="left-center" class="mb-2">{{$t('MRMR.Entry.Lead')}}</vs-divider>
                  <vs-col vs-w="5" class="mr-5">
                  <ValidationProvider :name="$t('MRMR.Entry.Grade')" rules="decimal:9|min_value:0" v-slot="{ errors }">
                    <validated-input
                        :label="$t('MRMR.Entry.Grade')"
                      v-model="entryLeadGrade"
                      :disabled="!useGradeEntry"
                    ></validated-input>
                    <validation-message
                      >{{ errors[0] }}</validation-message>
                  </ValidationProvider>
                  
                  <ValidationProvider :name="$t('MRMR.Entry.Kilograms')" rules="decimal:9|min_value:0" v-slot="{ errors }">
                    <validated-input
                      :label="$t('MRMR.Entry.Kilograms')"
                      v-model="entryLeadKg"
                      :disabled="useGradeEntry"
                    ></validated-input>
                    <validation-message
                      >{{ errors[0] }}</validation-message>
                  </ValidationProvider>
                </vs-col>
                <vs-col vs-w="6" v-if="previousLeadGrade" >
                   <form-label>{{$t('MRMR.Entry.PreviousGrade')}}</form-label>
                   <span class="previousGrade">{{ previousLeadGrade | toFriendlyGrade }}</span>
                </vs-col>
              </vs-row>
              <vs-row class="mb-1" vs-type="flex" vs-justify="space-between">
                <vs-divider position="left-center" class="mb-2">{{$t('MRMR.Entry.Zinc')}}</vs-divider>
                  <vs-col vs-w="5" class="mr-5">
                  <ValidationProvider :name="$t('MRMR.Entry.Grade')" rules="decimal:9|min_value:0" v-slot="{ errors }">
                    <validated-input
                        :label="$t('MRMR.Entry.Grade')"
                      v-model="entryZincGrade"
                      :disabled="!useGradeEntry"
                    ></validated-input>
                    <validation-message
                      >{{ errors[0] }}</validation-message>
                  </ValidationProvider>
                  
                  <ValidationProvider :name="$t('MRMR.Entry.Kilograms')" rules="decimal:9|min_value:0" v-slot="{ errors }">
                    <validated-input
                      :label="$t('MRMR.Entry.Kilograms')"
                      v-model="entryZincKg"
                      :disabled="useGradeEntry"
                    ></validated-input>
                    <validation-message
                      >{{ errors[0] }}</validation-message>
                  </ValidationProvider>
                </vs-col>
                <vs-col vs-w="6" v-if="previousZincGrade" >
                   <form-label>{{$t('MRMR.Entry.PreviousGrade')}}</form-label>
                   <span class="previousGrade">{{ previousZincGrade | toFriendlyGrade }}</span>
                </vs-col>
              </vs-row>
            </form> 
          </ValidationObserver> 
        </tab-content>
      </form-wizard>
    </ValidationObserver>
  </sidebar>
</template>

<script>
import ElementEnum from '@/app/shared/ElementEnum';
import { FormWizard, TabContent } from "vue-form-wizard";
import vSelect from 'vue-select'
import {
  FormLabel,
  ValidatedInput,
  ValidationMessage,
  Sidebar
} from "../../../../shared/components";
import ResourceTypeEnum from "../../ResourceTypeEnum.js";
import MiningTypeEnum from "../../MiningTypeEnum.js";
import VueNumeric from 'vue-numeric';

export default {
  props: {
    isSidebarActive: {
      type: Boolean,
      required: true
    },
    data: {
      type: Object,
      default: () => {}
    },
    previousStatementEntries : {
      type: Array
    },
    statementEntries : {
    },
    mrmrStatementHeaderId: {
      type: Number,
      required: true
    }
  },
  components: {
    FormWizard,
    TabContent,
    Sidebar,
    'v-select': vSelect,
    FormLabel,
    ValidatedInput,
    ValidationMessage,
    VueNumeric
  },
  data() {
    return {
      step1Error : '',
      model : { 
        id: 0,
        mrmrStatementHeaderId: 0,
        area: null,
        resourceType: ResourceTypeEnum.PotentialMineral,
        miningType : MiningTypeEnum.Underground,
        volume : 0,
        density : 0,
        tonnage: 0,
        grades: [],
        elements: []
      },
      tonnageMode: false,
      resourceTypes: [
        { id:ResourceTypeEnum.PotentialMineral, name: this.$t("MRMR.ResourceType." + ResourceTypeEnum.PotentialMineral) },
        { id:ResourceTypeEnum.Inferred, name: this.$t("MRMR.ResourceType." + ResourceTypeEnum.Inferred) },
        { id:ResourceTypeEnum.Indicated, name: this.$t("MRMR.ResourceType." + ResourceTypeEnum.Indicated) },
        { id:ResourceTypeEnum.Measured, name: this.$t("MRMR.ResourceType." + ResourceTypeEnum.Measured) },
        { id:ResourceTypeEnum.Probable, name: this.$t("MRMR.ResourceType." + ResourceTypeEnum.Probable) },
        { id:ResourceTypeEnum.Proven, name: this.$t("MRMR.ResourceType." + ResourceTypeEnum.Proven) }
      ],
      miningTypes: [
        { id:MiningTypeEnum.Underground, name: this.$t("MRMR.MiningType." + MiningTypeEnum.Underground) },
        { id:MiningTypeEnum.OpenPit, name: this.$t("MRMR.MiningType." + MiningTypeEnum.OpenPit) }
      ],
      elements: [
        { id:ElementEnum.Gold, name: this.$t("Element." + ElementEnum.Gold) },
        { id:ElementEnum.Silver, name: this.$t("Element." + ElementEnum.Silver) },
        { id:ElementEnum.Copper, name: this.$t("Element." + ElementEnum.Copper) },
        { id:ElementEnum.Zinc, name: this.$t("Element." + ElementEnum.Zinc) },
        { id:ElementEnum.Lead, name: this.$t("Element." + ElementEnum.Lead) }
      ],
      entryGoldElementId: 0,
      entryGoldGrade: null,
      entryGoldGrams: null,
      entrySilverElementId: 0,
      entrySilverGrade: null,
      entrySilverGrams: null,
      entryCopperElementId: 0,
      entryCopperGrade: null,
      entryCopperKg: null,
      entryZincElementId: 0,
      entryZincGrade: null,
      entryZincKg: null,
      entryLeadElementId: 0,
      entryLeadGrade: null,
      entryLeadKg: null,
      previousGoldGrade: null,
      previousSilverGrade: null,
      previousCopperGrade: null,
      previousZincGrade: null,
      previousLeadGrade: null,
      useGradeEntry: false
    };
  },
  computed: {
    isSidebarActiveLocal: {
      get() {
        return this.isSidebarActive;
      },
      set(val) {
        if (!val) {
          this.$emit("closeSidebar");
        }
      }
    },
    isFormValid() {
      return !this.errors.any();
    },
    scrollbarTag() {
      return this.$store.getters.scrollbarTag;
    },
    suggestedAreas() {
      if (this.$props.previousStatementEntries !== null && this.$props.previousStatementEntries !== undefined) {
        const previousItems =  this.$props.previousStatementEntries.map(p => p.area);
        const actualItems =  this.$props.statementEntries.map(p => p.area);
        const items = previousItems.concat(actualItems);
        return [...new Set(items)];
      } else {
        return [];
      }
    },
    previousTonnageStats() {
      if (this.$props.previousStatementEntries !== null && this.$props.previousStatementEntries !== undefined 
        && this.model.area !== null && this.model.area !== "" && this.model.resourceType !== null && this.model.miningType !== null) {
        const previousEntry = this.$props.previousStatementEntries.find(e => e.area === this.model.area && e.resourceType === this.model.resourceType && e.miningType === this.model.miningType);
        if (previousEntry !== null && previousEntry !== undefined) {
          const diff = this.model.tonnage - previousEntry.tonnage;
          if (diff === 0) {
            return previousEntry.tonnage;    
          } else {
            return previousEntry.tonnage + " (" + diff + ")";
          }
        } else {
          return null;
        }
      } else {
        return null;
      }
    },
    previousGoldGradeStats() {
      return this.getGradeStats(this.previousGoldGrade, this.entryGoldGrade);
    },
    previousSilverGradeStats() {
      return this.getGradeStats(this.previousSilverGrade, this.entrySilverGrade);
    },
    area() { 
      return this.model.area; 
    },
    volume() { 
      return this.model.volume; 
    },
    density() { 
      return this.model.density; 
    },
    resourceType() { 
      return this.model.resourceType; 
    }
  },
  watch: {
    area() {
      this.getPreviousGrade();
    },
    resourceType() {
      this.getPreviousGrade();
    },
    density() {
      if (this.model.density !== null) {
        this.model.tonnage = this.model.volume * this.model.density;
      }
    },
    volume() {
      if (this.model.volume !== null) {
        this.model.tonnage = this.model.volume * this.model.density;
      }
    },
    entryGoldGrade() {
      if (!this.useGradeEntry) return;
      this.entryGoldGrams = this.calculateQuantityInGrams(this.entryGoldGrade);
    },
    entryGoldGrams() {
      if (this.useGradeEntry) return;
      this.entryGoldGrade = this.calculateGradeFromGrams(this.entryGoldGrams);
    },
    entrySilverGrade() {
      if (!this.useGradeEntry) return;
      this.entrySilverGrams = this.calculateQuantityInGrams(this.entrySilverGrade);    
    },
    entrySilverGrams() {
      if (this.useGradeEntry) return;
      this.entrySilverGrade = this.calculateGradeFromGrams(this.entrySilverGrams);
    },
    entryCopperGrade() {
      if (!this.useGradeEntry) return;      
      this.entryCopperKg = this.calculateQuantityInKg(this.entryCopperGrade);
    },
    entryCopperKg() {
      if (this.useGradeEntry) return;
      this.entryCopperGrade = this.calculateGradeFromKg(this.entryCopperKg);
    },
    entryLeadGrade() {
      if (!this.useGradeEntry) return;
      this.entryLeadKg = this.calculateQuantityInKg(this.entryLeadGrade);
    },
    entryLeadKg() {
      if (this.useGradeEntry) return;
      this.entryLeadGrade = this.calculateGradeFromKg(this.entryLeadKg);
    },
    entryZincGrade() {
      if (!this.useGradeEntry) return;
      this.entryZincKg = this.calculateQuantityInKg(this.entryZincGrade);
    },
    entryZincKg() {
      if (this.useGradeEntry) return;
      this.entryZincGrade = this.calculateGradeFromKg(this.entryZincKg);
    }
  },
  methods: {
    calculateQuantityInKg(elementGrade) {
      if (elementGrade === null) return null;
      return (elementGrade * this.model.tonnage * 10).toFixed(9);
    },
    calculateGradeFromKg(quantity) {
      if (quantity === null) return null;
      return (quantity / (this.model.tonnage * 10)).toFixed(9);
    },
    calculateQuantityInGrams(elementGrade) {
      if (elementGrade === null) return null;
      return (elementGrade * this.model.tonnage).toFixed(9);
    },
    calculateGradeFromGrams(quantity) {
      if (quantity === null) return null;
      return (quantity / this.model.tonnage).toFixed(9);
    },
    getGradeStats(previousGrade, elementGrade) {
      if (previousGrade !== null) {
        if (elementGrade !== null) {
          const diff = elementGrade - previousGrade;
          if (diff === 0) {
            return previousGrade;    
          } else {
            return previousGrade + " (" + diff + ")";
          }
        } else {
          return previousGrade;
        }
      } else {
        return null;
      }
    },
    newData() {
      this.initValues();
      this.step1Error = '';
      this.$refs.wizard.reset();
    },
    getPreviousGrade() {
      if (this.$props.previousStatementEntries !== null && this.$props.previousStatementEntries !== undefined 
        && this.model.area !== null && this.model.area !== "" && this.model.resourceType !== null && this.model.miningType !== null) {
        const previousEntry = this.$props.previousStatementEntries.find(e => e.area === this.model.area && e.resourceType === this.model.resourceType && e.miningType === this.model.miningType);
        if (previousEntry !== null && previousEntry !== undefined) {
          this.previousGoldGrade = this.getElementGrade(previousEntry.grades,  ElementEnum.Gold);
          this.previousSilverGrade = this.getElementGrade(previousEntry.grades,  ElementEnum.Silver);
          this.previousCopperGrade = this.getElementGrade(previousEntry.grades,  ElementEnum.Copper);
          this.previousZincGrade = this.getElementGrade(previousEntry.grades,  ElementEnum.Zinc);
          this.previousLeadGrade = this.getElementGrade(previousEntry.grades,  ElementEnum.Lead)
        } else {
          this.resetPreviousGrades();
        }
      } else {
        this.resetPreviousGrades();
      }
    },
    resetPreviousGrades() {
      this.previousGoldGrade = null;
      this.previousSilverGrade = null;
      this.previousCopperGrade = null;
      this.previousZincGrade = null;
      this.previousLeadGrade = null;
    },
    editData() {
      this.model = JSON.parse(JSON.stringify(this.data));
      this.tonnageMode = this.model.density === null && this.model.volume === null;

      const grades = this.model.grades;
      this.entryGoldElementId = this.getElementId(grades, ElementEnum.Gold);
      this.entryGoldGrade = this.getElementGrade(grades,  ElementEnum.Gold);
      this.entryGoldGrams = this.calculateQuantityInGrams(this.entryGoldGrade);
      this.entrySilverElementId = this.getElementId(grades, ElementEnum.Silver);
      this.entrySilverGrade = this.getElementGrade(grades, ElementEnum.Silver);
      this.entrySilverGrams = this.calculateQuantityInGrams(this.entrySilverGrade);
      this.entryCopperElementId = this.getElementId(grades, ElementEnum.Copper);
      this.entryCopperGrade = this.getElementGrade(grades, ElementEnum.Copper);
      this.entryCopperKg = this.calculateQuantityInKg(this.entryCopperGrade);
      this.entryLeadElementId = this.getElementId(grades, ElementEnum.Lead);
      this.entryLeadGrade = this.getElementGrade(grades, ElementEnum.Lead);
      this.entryLeadKg = this.calculateQuantityInKg(this.entryLeadGrade);
      this.entryZincElementId = this.getElementId(grades, ElementEnum.Zinc);
      this.entryZincGrade = this.getElementGrade(grades, ElementEnum.Zinc);
      this.entryZincKg = this.calculateQuantityInKg(this.entryZincGrade);

      this.$refs.wizard.reset();
    },
    closeSidebar() {
      this.$emit("closeSidebar");
    },
    initValues() {
      if (this.data.id) return;
        
      this.step1Error = '';
      this.model.id = 0
      this.model.mmmrStatementHeaderId = this.mmmrStatementHeaderId;
      this.model.area = null;
      this.model.resourceType = ResourceTypeEnum.PotentialMineral;
      this.model.miningType = MiningTypeEnum.Underground;
      this.tonnageMode = false;
      this.model.volume = null;
      this.model.density = null;
      this.model.tonnage = null;
      this.entryGoldElementId = 0;
      this.entryGoldGrade = null;
      this.entryGoldGrams = null;
      this.entrySilverElementId = 0;
      this.entrySilverGrade = null;
      this.entrySilverGrams = null;
      this.entryCopperElementId = 0;
      this.entryCopperGrade = null;
      this.entryCopperKg = null;
      this.entryZincElementId = 0;
      this.entryZincGrade = null;
      this.entryZincKg = null;
      this.entryLeadElementId = 0;
      this.entryLeadGrade = null;
      this.entryLeadKg = null;
      this.model.elements = [];
    },
  
    getElementId(grades, elementId) {
      if (grades !== null) {
        const element = grades.find(r => r.element === elementId)
        if (element) {
          return element.id;
        }
        else {
          return 0;
        }
      }
      else {
        return 0;
      }
    },
    getElementGrade(grades, elementId) {
      if (grades !== null) {
        const element = grades.find(r => r.element === elementId)
        if (element) {
          return element.grade;
        }
        else {
          return null;
        }
      }
      else {
        return null;
      }
    },
    
    defineResult(elementId, id, grade, grades) {
      if (grade !== null && grade !== '') {
        grades.push({
          id,
          mrmrStatementEntryId : this.model.id,
          element : elementId,
          grade
        })
      }
    },
    validateStep1() {
      return new Promise((resolve, reject) => {
        this.$refs.step1Observer.validate().then(result => {
          if (result) {
            const actualStatement = this.statementEntries.find(e => e.area === this.model.area && e.resourceType === this.model.resourceType && e.miningType === this.model.miningType && (this.model.id === 0 || e.id !== this.model.id));
            if (actualStatement !== undefined) {
              this.step1Error = this.$t('MRMR.Entry.DuplicatedEntryException');
              reject("Already existing");
            } else {
              resolve(true);
            }
          } else {
            reject("correct all values");
          }
        });
      });
    },
    validateStep2() {
      return new Promise((resolve, reject) => {
        this.$refs.step2Observer.validate().then(result => {
          if (result) {
            resolve(true);
          } else {
            reject("correct all values");
          }
        });
      });
    },
    submitData() {

      // clear density and volume if tonnage was forced
      if (this.tonnageMode === true) {
        this.model.density = null;
        this.model.volume = null;
      }
      const grades = this.model.grades;
      this.defineResult(ElementEnum.Gold, this.entryGoldElementId, this.entryGoldGrade, grades);
      this.defineResult(ElementEnum.Silver, this.entrySilverElementId, this.entrySilverGrade, grades);
      this.defineResult(ElementEnum.Copper, this.entryCopperElementId, this.entryCopperGrade, grades);
      this.defineResult(ElementEnum.Lead, this.entryLeadElementId, this.entryLeadGrade, grades);
      this.defineResult(ElementEnum.Zinc, this.entryZincElementId, this.entryZincGrade, grades);
      
      if (this.model.id !== null && this.model.id > 0) {
        this.$emit("onEntryUpdated", this.model);
      } else {
        this.$emit("onEntryInserted", this.model);
      }
    }
  }
};
</script>
<style lang="scss">
  .switch_tonnage {
    width: 165px !important;
  }

  .switch_grade {
    width: 100px !important;
  }
  .previousGrade {
    padding-left: 5px;
    display: block;
    font-family: inherit;
    font-weight: 500;
    line-height: 1.2;
    color: #2c2c2c;
    font-size: 18.48px;
    padding-top: 5px;
  }
</style>

